import React from 'react';
import type { ProductionLineFiltersProps } from '../../types/components';
import { Search, Filter, Calendar } from 'lucide-react';

export const ProductionLineFilters: React.FC<ProductionLineFiltersProps> = ({
  searchTerm,
  selectedDate,
  jobTypeFilter,
  onSearchChange,
  onDateChange,
  onJobTypeChange,
  productTypeFilter = 'all',
  onProductTypeFilterChange,
  showProductTypeFilter = false,
  showSpacerFilters = false,
  spacerBarTypeFilter = 'all',
  spacerBarTypeOptions = [],
  onSpacerBarTypeFilterChange,
  spacerBarColorFilter = 'all',
  spacerBarColorOptions = [],
  onSpacerBarColorFilterChange,
  spacerBarThicknessFilter = 'all',
  spacerBarThicknessOptions = [],
  onSpacerBarThicknessFilterChange,
  spacerBar2TypeFilter = 'all',
  spacerBar2TypeOptions = [],
  onSpacerBar2TypeFilterChange,
  spacerBar2ColorFilter = 'all',
  spacerBar2ColorOptions = [],
  onSpacerBar2ColorFilterChange,
  spacerBar2ThicknessFilter = 'all',
  spacerBar2ThicknessOptions = [],
  onSpacerBar2ThicknessFilterChange,
  className = '',
}) => {
  return (
    <div className={`mb-6 ${className}`}>
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[300px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search jobs & orders (names, address, refs, SKU, notes, rack, bar/spacer fields; digits match sizes/qty)…"
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="form-input pl-10 w-full"
                />
              </div>
            </div>

            {/* Filters Label */}
            <div className="hidden sm:flex items-center gap-2 text-gray-700">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium">Filters:</span>
            </div>

            {/* Date Filter */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => onDateChange(e.target.value)}
                className="form-input w-auto"
              />
            </div>

            {/* Job Type Filter */}
            <select
              value={jobTypeFilter}
              onChange={(e) => onJobTypeChange(e.target.value)}
              className="form-input w-auto"
            >
              <option value="all">All Types</option>
              <option value="ga">GA (Glass Arrival)</option>
              <option value="gc">GC (Glass Cutting)</option>
              <option value="gap">GAP (Glass Processing)</option>
            </select>

            {showProductTypeFilter && onProductTypeFilterChange ? (
              <select
                value={productTypeFilter}
                onChange={(e) => onProductTypeFilterChange(e.target.value)}
                className="form-input w-auto"
                aria-label="Product type"
              >
                <option value="all">All product types</option>
                <option value="single">Single glazed</option>
                <option value="double">Double glazed</option>
                <option value="triple">Triple glazed</option>
                <option value="mirror">Mirror</option>
              </select>
            ) : null}
          </div>

          {showSpacerFilters &&
          onSpacerBarTypeFilterChange &&
          onSpacerBarColorFilterChange &&
          onSpacerBarThicknessFilterChange &&
          onSpacerBar2TypeFilterChange &&
          onSpacerBar2ColorFilterChange &&
          onSpacerBar2ThicknessFilterChange ? (
            <div className="pt-2 border-t border-gray-100">
              <div className="text-sm font-medium text-gray-700 mb-2">Spacer filters</div>
              {/*
                .form-input is w-full — in flex-wrap each select took full row width.
                Grid cells constrain width: 3 columns on small/medium, 6 on large screens.
              */}
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-3 items-stretch">
                <div className="min-w-0">
                  <select
                    value={spacerBarTypeFilter}
                    onChange={(e) => onSpacerBarTypeFilterChange(e.target.value)}
                    className="form-input w-full min-w-0 text-sm"
                    aria-label="Spacer type"
                  >
                    <option value="all">All spacer types</option>
                    {spacerBarTypeOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <select
                    value={spacerBarColorFilter}
                    onChange={(e) => onSpacerBarColorFilterChange(e.target.value)}
                    className="form-input w-full min-w-0 text-sm"
                    aria-label="Spacer colour"
                  >
                    <option value="all">All spacer colours</option>
                    {spacerBarColorOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <select
                    value={spacerBarThicknessFilter}
                    onChange={(e) => onSpacerBarThicknessFilterChange(e.target.value)}
                    className="form-input w-full min-w-0 text-sm"
                    aria-label="Spacer thickness"
                  >
                    <option value="all">All thicknesses</option>
                    {spacerBarThicknessOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt} mm
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <select
                    value={spacerBar2TypeFilter}
                    onChange={(e) => onSpacerBar2TypeFilterChange(e.target.value)}
                    className="form-input w-full min-w-0 text-sm"
                    aria-label="Spacer2 type"
                  >
                    <option value="all">All spacer2 types</option>
                    {spacerBar2TypeOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <select
                    value={spacerBar2ColorFilter}
                    onChange={(e) => onSpacerBar2ColorFilterChange(e.target.value)}
                    className="form-input w-full min-w-0 text-sm"
                    aria-label="Spacer2 colour"
                  >
                    <option value="all">All spacer2 colours</option>
                    {spacerBar2ColorOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <select
                    value={spacerBar2ThicknessFilter}
                    onChange={(e) => onSpacerBar2ThicknessFilterChange(e.target.value)}
                    className="form-input w-full min-w-0 text-sm"
                    aria-label="Spacer2 thickness"
                  >
                    <option value="all">All spacer2 thickness</option>
                    {spacerBar2ThicknessOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt} mm
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
